import { zh } from '../../utils/zh';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, Bot, CheckCircle2, StopCircle, FlaskConical, Mail,
    Plus, RefreshCw, Send, ShieldCheck, Trash2, Users, XCircle,
} from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { SendChannelNav } from '../bulk-send/SendChannelNav';

interface InternalMailbox {
    email: string;
    displayName: string;
    domain: string;
    region: string;
}

interface InternalJob {
    id: string;
    status: string;
    recipientMode: 'round_robin' | 'single';
    recipientEmail: string | null;
    requestedCount: number;
    selectedCount: number;
    processedCount: number;
    sentCount: number;
    failedCount: number;
    uncertainCount: number;
    skippedCount: number;
    removedCount: number;
    delayMs: number;
    lastError: string | null;
    createdAt: string;
    finishedAt: string | null;
}

interface InternalAttempt {
    id: string;
    senderEmail: string;
    recipientEmail: string;
    ordinal: number;
    status: string;
    errorCategory: string | null;
    errorMessage: string | null;
    accountRemoved: boolean;
}

interface InternalOverview {
    mode: 'live';
    dryRunAvailable: false;
    direction: 'outlook_pool_to_internal_spacemail';
    externalRecipientsAllowed: false;
    excelContactsUsed: false;
    pool: { total: number; active: number; oauthReady: number; errors: number };
    recipients: InternalMailbox[];
    limits: {
        maxAccountsPerJob: number;
        maxSelectableNow: number;
        minDelayMs: number;
        defaultDelayMs: number;
        perAccountPerJob: 1;
        concurrency: 1;
    };
    activeJob: InternalJob | null;
}

const PREVIEW_OVERVIEW: InternalOverview = {
    mode: 'live', dryRunAvailable: false,
    direction: 'outlook_pool_to_internal_spacemail', externalRecipientsAllowed: false, excelContactsUsed: false,
    pool: { total: 1010, active: 999, oauthReady: 999, errors: 11 },
    recipients: [
        { email: 'alex.brennan@lawhelper.eu', displayName: 'Alex Brennan', domain: 'lawhelper.eu', region: 'eu' },
        { email: 'amina.diallo@lawhelper.org', displayName: 'Amina Diallo', domain: 'lawhelper.org', region: 'ae' },
        { email: 'ana@lawhelper.lat', displayName: 'Ana', domain: 'lawhelper.lat', region: 'lat' },
    ],
    limits: { maxAccountsPerJob: 1000, maxSelectableNow: 999, minDelayMs: 1500, defaultDelayMs: 2000, perAccountPerJob: 1, concurrency: 1 },
    activeJob: null,
};

function jobTone(status: string): string {
    if (status === 'completed') return 'success';
    if (['failed', 'cancelled'].includes(status)) return 'danger';
    if (['queued', 'running', 'cancelling'].includes(status)) return 'warning';
    return 'info';
}

async function readApi<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await authFetch(path, options);
    const data = await response.json();
    if (!response.ok || data.success === false) throw new Error(data.error || `请求失败（${response.status}）`);
    return data as T;
}

export function InternalSendPage({ previewMode = false }: { previewMode?: boolean }) {
    const [overview, setOverview] = useState<InternalOverview | null>(previewMode ? PREVIEW_OVERVIEW : null);
    const [jobs, setJobs] = useState<InternalJob[]>([]);
    const [selectedJobId, setSelectedJobId] = useState('');
    const [attempts, setAttempts] = useState<InternalAttempt[]>([]);
    const [count, setCount] = useState(3);
    const [recipientMode, setRecipientMode] = useState<'round_robin' | 'single'>('round_robin');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [subjectTemplate, setSubjectTemplate] = useState('Internal delivery check {{sequence}}/{{total}}');
    const [bodyTemplate, setBodyTemplate] = useState('Hello,\n\nThis is an internal delivery test from {{senderEmail}}.\nJob: {{jobId}}\nRecipient: {{recipientEmail}}');
    const [customFields, setCustomFields] = useState<Array<{ id: string; key: string; value: string }>>([
        { id: crypto.randomUUID(), key: 'Environment', value: 'Internal QA' },
    ]);
    const [confirmation, setConfirmation] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(() => `internal-ui-${crypto.randomUUID()}`);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const activeJob = jobs.find((job) => ['queued', 'running', 'cancelling'].includes(job.status)) || overview?.activeJob || null;
    const selectedJob = jobs.find((job) => job.id === selectedJobId) || activeJob || null;
    const confirmationPhrase = `SEND INTERNAL ${count}`;
    const maxCount = Math.max(overview?.limits.maxSelectableNow || 1, 1);

    const loadDashboard = useCallback(async () => {
        if (previewMode) return;
        try {
            const [overviewData, jobData] = await Promise.all([
                readApi<{ overview: InternalOverview }>('/warmup/internal-test/overview'),
                readApi<{ jobs: InternalJob[] }>('/warmup/internal-test/jobs?limit=25'),
            ]);
            setOverview(overviewData.overview);
            setJobs(jobData.jobs || []);
            const currentId = selectedJobId || overviewData.overview.activeJob?.id || jobData.jobs?.[0]?.id || '';
            if (currentId) setSelectedJobId(currentId);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    }, [previewMode, selectedJobId]);

    const loadAttempts = useCallback(async (jobId: string) => {
        if (previewMode || !jobId) return;
        try {
            const data = await readApi<{ attempts: InternalAttempt[] }>(`/warmup/internal-test/jobs/${jobId}/attempts?limit=1000`);
            setAttempts(data.attempts || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    }, [previewMode]);

    useEffect(() => { void loadDashboard(); }, [loadDashboard]);
    useEffect(() => { if (selectedJobId) void loadAttempts(selectedJobId); }, [loadAttempts, selectedJobId]);
    useEffect(() => {
        if (previewMode) return;
        const interval = window.setInterval(() => { void loadDashboard(); if (selectedJobId) void loadAttempts(selectedJobId); }, activeJob ? 2500 : 15000);
        return () => window.clearInterval(interval);
    }, [activeJob, loadAttempts, loadDashboard, previewMode, selectedJobId]);

    useEffect(() => {
        if (!overview?.recipients.length) return;
        if (!overview.recipients.some((mailbox) => mailbox.email === recipientEmail)) setRecipientEmail(overview.recipients[0].email);
    }, [overview, recipientEmail]);

    const previewMessage = useMemo(() => {
        const fields = Object.fromEntries(customFields.filter((field) => field.key.trim()).map((field) => [field.key.trim(), field.value]));
        const values: Record<string, string> = {
            senderEmail: 'outlook-account@example.com', recipientEmail: recipientEmail || 'internal@lawhelper.*',
            sequence: '1', total: String(count), jobId: 'generated-at-start',
        };
        const render = (value: string) => value.replace(/{{\s*([^{}]+?)\s*}}/g, (_match, raw: string) => {
            const key = raw.trim();
            return key.startsWith('custom.') ? fields[key.slice(7)] || '' : values[key] || `{{${key}}}`;
        });
        return { subject: `[Internal Outlook test] ${render(subjectTemplate)}`, body: render(bodyTemplate) };
    }, [bodyTemplate, count, customFields, recipientEmail, subjectTemplate]);

    async function startJob() {
        if (previewMode) return;
        setBusy('start'); setError(''); setNotice('');
        try {
            const fields: Record<string, string> = {};
            for (const field of customFields) {
                const key = field.key.trim();
                if (!key) continue;
                if (Object.prototype.hasOwnProperty.call(fields, key)) throw new Error(`自定义变量名称重复：${key}`);
                fields[key] = field.value;
            }
            const data = await readApi<{ job: InternalJob; reused: boolean }>('/warmup/internal-test/jobs', {
                method: 'POST',
                body: JSON.stringify({
                    count, recipientMode, recipientEmail: recipientMode === 'single' ? recipientEmail : undefined,
                    subjectTemplate, bodyTemplate, customFields: fields,
                    delayMs: overview?.limits.defaultDelayMs || 2000,
                    confirmationPhrase, internalOnlyAcknowledged: acknowledged, idempotencyKey,
                }),
            });
            setSelectedJobId(data.job.id);
            setConfirmation(''); setAcknowledged(false);
            setIdempotencyKey(`internal-ui-${crypto.randomUUID()}`);
            setNotice(data.reused ? `已打开现有任务 ${data.job.id}。` : `内部发送任务 ${data.job.id} 已启动。`);
            await loadDashboard();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally { setBusy(''); }
    }

    async function cancelJob(jobId: string) {
        if (previewMode || !window.confirm("停止本次内部测试中所有尚未发送的邮件？已经开始发送的邮件可能仍会完成。")) return;
        setBusy('cancel'); setError('');
        try {
            await readApi(`/warmup/internal-test/jobs/${jobId}/cancel`, { method: 'POST', body: '{}' });
            setNotice("排队中的内部测试邮件已取消。");
            await loadDashboard();
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
        finally { setBusy(''); }
    }

    const failures = attempts.filter((attempt) => ['failed', 'uncertain'].includes(attempt.status));
    const progress = selectedJob?.selectedCount ? Math.round(selectedJob.processedCount / selectedJob.selectedCount * 100) : 0;

    return (
        <div className="bulk-send-page internal-send-page">
            <SendChannelNav />
            {previewMode && <div className="bulk-preview-banner"><ShieldCheck size={18} /><div><strong>本地示例预览</strong><span>这是界面预览；真实发送按钮已锁定，不会发出邮件。</span></div></div>}

            <div className="bulk-header">
                <div>
                    <div className="bulk-eyebrow"><FlaskConical size={14} /> 内部测试邮件管理</div>
                    <h2 className="page-title">Outlook 账号池 → 内部 SpaceMail 邮箱</h2>
                    <p className="page-subtitle">每个 Outlook 账号在一个任务中最多发送一封；收件人只能从内部邮箱下拉框选择。</p>
                </div>
                <button className="btn btn-outline" onClick={() => void loadDashboard()} disabled={previewMode || !!busy}><RefreshCw size={14} /> 刷新</button>
            </div>

            <div className="bulk-policy-banner internal-policy">
                <ShieldCheck size={18} />
                <div><strong>仅向内部邮箱发送。</strong> 不读取外部 Excel，不接受手工输入的外部收件人；通过 Microsoft Graph 逐封真实发送。</div>
                <span className="bulk-mode live">实际发送</span>
            </div>
            {error && <div className="bulk-alert danger"><XCircle size={16} /><span>{error}</span><button onClick={() => setError('')}>×</button></div>}
            {notice && <div className="bulk-alert success"><CheckCircle2 size={16} /><span>{notice}</span><button onClick={() => setNotice('')}>×</button></div>}

            <div className="bulk-stats">
                <div className="bulk-stat blue"><span><Mail size={18} /></span><div><small>Outlook 账号池</small><strong>{overview?.pool.total ?? '—'}</strong><em>{overview?.pool.oauthReady ?? 0} 已完成授权</em></div></div>
                <div className="bulk-stat green"><span><Users size={18} /></span><div><small>内部收件人</small><strong>{overview?.recipients.length ?? '—'}</strong><em>仅限已配置的 LawHelper 邮箱</em></div></div>
                <div className="bulk-stat purple"><span><Send size={18} /></span><div><small>每个账号</small><strong>1</strong><em>每个任务 1 封 · 逐封发送</em></div></div>
                <div className="bulk-stat amber"><span><AlertTriangle size={18} /></span><div><small>账号池异常</small><strong>{overview?.pool.errors ?? '—'}</strong><em>授权失败的账号退出可用池</em></div></div>
            </div>

            <div className="internal-layout">
                <section className="card bulk-section internal-config-card">
                    <div className="bulk-section-head"><div><span className="bulk-step">1</span><div><h3>配置内部测试</h3><p>此处使用内部邮箱，不需要 Excel 或外部联系人列表。</p></div></div><span className="internal-only">Outlook → 内部 SpaceMail</span></div>
                    <div className="bulk-two-col">
                        <label className="bulk-field"><span>Outlook 账号数量</span><input className="input" type="number" min={1} max={maxCount} value={count} onChange={(event) => { setCount(Math.min(Math.max(Number(event.target.value) || 1, 1), maxCount)); setConfirmation(''); }} /></label>
                        <label className="bulk-field"><span>内部收件方式</span><select className="input" value={recipientMode} onChange={(event) => setRecipientMode(event.target.value as 'round_robin' | 'single')}><option value="round_robin">轮换发送至内部邮箱</option><option value="single">发送至指定内部邮箱</option></select></label>
                    </div>
                    {recipientMode === 'single' && <label className="bulk-field"><span>收件人（仅限已配置的内部邮箱）</span><select className="input" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)}>{overview?.recipients.map((mailbox) => <option value={mailbox.email} key={mailbox.email}>{mailbox.email} · {mailbox.region.toUpperCase()}</option>)}</select></label>}
                    {recipientMode === 'round_robin' && <div className="internal-recipient-list">{overview?.recipients.map((mailbox) => <span key={mailbox.email}>{mailbox.email}</span>)}</div>}

                    <label className="bulk-field"><span>主题模板</span><input className="input" value={subjectTemplate} onChange={(event) => setSubjectTemplate(event.target.value)} /></label>
                    <label className="bulk-field"><span>正文模板</span><textarea className="input bulk-message internal-message" value={bodyTemplate} onChange={(event) => setBodyTemplate(event.target.value)} /></label>
                    <div className="bulk-detected-fields"><div><strong>可用变量</strong><span>系统会为每个 Outlook 发件人分别填入对应变量。</span></div><div>{['{{senderEmail}}','{{recipientEmail}}','{{sequence}}','{{total}}','{{jobId}}'].map((token) => <code key={token}>{token}</code>)}</div></div>

                    <div className="bulk-test-fields">
                        <div className="bulk-test-field-title"><span>自定义变量 · 使用格式 {'{{custom.Field}}'}</span><button onClick={() => setCustomFields((current) => [...current, { id: crypto.randomUUID(), key: '', value: '' }])}><Plus size={12} /> 添加变量</button></div>
                        {customFields.map((field) => <div className="bulk-test-field-row" key={field.id}><input className="input" placeholder="变量名称" value={field.key} onChange={(event) => setCustomFields((current) => current.map((item) => item.id === field.id ? { ...item, key: event.target.value } : item))} /><input className="input" placeholder="变量内容" value={field.value} onChange={(event) => setCustomFields((current) => current.map((item) => item.id === field.id ? { ...item, value: event.target.value } : item))} /><button title="移除" onClick={() => setCustomFields((current) => current.filter((item) => item.id !== field.id))}><Trash2 size={13} /></button></div>)}
                    </div>
                    <div className="bulk-message-preview"><div className="bulk-preview-head"><span><Mail size={14} /> 首封邮件预览</span><small>仅限内部收件人</small></div><strong>{previewMessage.subject}</strong><pre>{previewMessage.body}</pre></div>
                </section>

                <aside className="card bulk-section internal-launch-card">
                    <div className="launch-kicker"><Bot size={13} /> 人工操作或授权 AI 接口</div>
                    <h3>开始内部发送测试</h3>
                    <p>此功能会实际发送测试邮件。本地预览无法启动任务。</p>
                    <div className="launch-summary"><div><span>发送方向</span><strong>Outlook → 内部 SpaceMail</strong></div><div><span>账号数量</span><strong>{count}</strong></div><div><span>每个账号发送封数</span><strong>1</strong></div><div><span>同时发送数量</span><strong>1 封（逐封发送）</strong></div><div><span>外部收件人</span><strong>已禁止</strong></div></div>
                    <label className="bulk-check"><input type="checkbox" checked={acknowledged} disabled={previewMode || !!activeJob} onChange={(event) => setAcknowledged(event.target.checked)} /><span>我确认，此测试将实际发送邮件到已配置的自有内部邮箱。</span></label>
                    <div className="live-confirm"><AlertTriangle size={14} /><div><strong>请输入完整确认文字</strong><span><code>{confirmationPhrase}</code></span></div><input className="input" value={confirmation} disabled={previewMode || !!activeJob} onChange={(event) => setConfirmation(event.target.value)} placeholder={confirmationPhrase} /></div>
                    <button className="btn btn-success internal-start" disabled={previewMode || !!activeJob || !overview?.recipients.length || confirmation !== confirmationPhrase || !acknowledged || busy === 'start'} onClick={() => void startJob()}><Send size={14} /> {previewMode ? "登录后开始测试" : activeJob ? "已有内部测试任务正在运行" : busy === 'start' ? "正在启动…" : `开始发送 ${count} 封内部测试邮件`}</button>
                    <div className="ai-boundary">AI 接口使用相同的内部收件人限制与逐封发送规则，不会返回账号密码或授权令牌。</div>
                </aside>
            </div>

            <section className="card bulk-section internal-jobs-card">
                <div className="bulk-section-head"><div><span className="bulk-step">2</span><div><h3>内部任务与失败记录</h3><p>授权失败时，系统会自动将 Outlook 账号标记为异常。</p></div></div>{selectedJob && ['queued','running','cancelling'].includes(selectedJob.status) && <button className="btn btn-outline btn-sm" disabled={busy === 'cancel'} onClick={() => void cancelJob(selectedJob.id)}><StopCircle size={13} /> 停止排队任务</button>}</div>
                {previewMode && <div className="internal-empty">本地预览不会创建实际发送任务。</div>}
                {!previewMode && jobs.length === 0 && <div className="internal-empty">暂无内部测试任务。</div>}
                {jobs.length > 0 && <div className="job-list">{jobs.map((job) => <button className={`job-row ${selectedJobId === job.id ? 'selected' : ''}`} key={job.id} onClick={() => setSelectedJobId(job.id)}><span className={`job-status ${jobTone(job.status)}`}>{zh(job.status)}</span><span className="job-copy"><strong>{job.id}</strong><small>{new Date(job.createdAt).toLocaleString()}</small></span><span className="job-progress"><span><i style={{ width: `${job.selectedCount ? Math.round(job.processedCount / job.selectedCount * 100) : 0}%` }} /></span><small>{job.processedCount}/{job.selectedCount} · {job.sentCount} 封已发送 · {job.failedCount + job.uncertainCount} 项问题</small></span><span className="internal-removed">{job.removedCount} 个已移除</span></button>)}</div>}

                {selectedJob && <div className="internal-job-detail"><div><strong>{progress}% 已完成</strong><span>{selectedJob.sentCount} 封已发送 · {selectedJob.failedCount} 封失败 · {selectedJob.uncertainCount} 封结果不确定 · {selectedJob.removedCount} 个账号已移出账号池</span></div><div className="internal-progress"><i style={{ width: `${progress}%` }} /></div></div>}
                {failures.length > 0 && <div className="attempt-table-wrap"><div className="attempt-title"><span>发送失败或结果不确定的 Outlook 账号</span><span>{failures.length}</span></div><table className="data-table"><thead><tr><th>#</th><th>Outlook 发件人</th><th>内部收件人</th><th>状态</th><th>账号池处理</th><th>原因</th></tr></thead><tbody>{failures.map((attempt) => <tr key={attempt.id}><td>{attempt.ordinal}</td><td>{attempt.senderEmail}</td><td>{attempt.recipientEmail}</td><td><span className={`job-status ${jobTone(attempt.status)}`}>{zh(attempt.status)}</span></td><td>{attempt.accountRemoved ? "已移出可用账号池" : "保留待审核"}</td><td className="attempt-reason" title={attempt.errorMessage || ''}>{attempt.errorCategory}: {attempt.errorMessage}</td></tr>)}</tbody></table></div>}
            </section>
        </div>
    );
}
